import { useState, useMemo } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import DonationSuccessModal from "./DonationSuccessModal";
import CopyLinkButton from "./CopyLinkButton";
import { siteUrl } from "../lib/siteConfig";

const CURRENCIES = ["USD", "GBP", "EUR"];
const CURRENCY_SYMBOL = { USD: "$", GBP: "£", EUR: "€" };
const ONETIME_PRESETS = [10, 25, 50, 100, 250];
const MONTHLY_PRESETS = [30, 50, 100, 200, 500];
const MONTHLY_MIN = 30;

const PATRON_DISCLAIMER =
  "International Patrons — You're supporting our storytelling and impact work as a member of our creative community. Your membership includes exclusive content and member-only benefits. This is a paid digital membership, not a charitable donation, and is not eligible for Indian tax benefits.";

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InternationalDonate({ defaultMode = "one-time" }) {
  const [mode, setMode] = useState(defaultMode); // "one-time" | "monthly"
  const [currency, setCurrency] = useState("USD");
  const [amount, setAmount] = useState(defaultMode === "monthly" ? 50 : 50);
  const [custom, setCustom] = useState("");
  const [donor, setDonor] = useState({ name: "", email: "", phone: "", address: "", inspired_by: "" });
  const [successModal, setSuccessModal] = useState({ open: false });

  const finalAmount = useMemo(() => {
    const n = custom ? parseFloat(custom) : amount;
    return isNaN(n) || n < 1 ? 0 : n;
  }, [amount, custom]);

  const monthlyMinFail = mode === "monthly" && finalAmount > 0 && finalAmount < MONTHLY_MIN;

  const presets = mode === "monthly" ? MONTHLY_PRESETS : ONETIME_PRESETS;

  const oneTimeOptions = {
    "client-id": PAYPAL_CLIENT_ID,
    currency,
    intent: "capture",
    "enable-funding": "card,venmo",
    "disable-funding": "paylater",
    components: "buttons,funding-eligibility",
  };

  const subscriptionOptions = {
    "client-id": PAYPAL_CLIENT_ID,
    currency,
    intent: "subscription",
    vault: true,
    "disable-funding": "paylater",
    components: "buttons",
  };

  const validateDonor = () => {
    if (!donor.name.trim() || !donor.email.trim()) {
      toast.error("Please enter your name and email above before continuing.");
      return false;
    }
    if (!donor.address.trim()) {
      toast.error("Please enter your postal address before continuing.");
      return false;
    }
    if (finalAmount < 1) {
      toast.error("Please choose or enter a valid amount.");
      return false;
    }
    if (mode === "monthly" && finalAmount < MONTHLY_MIN) {
      toast.error(`Minimum monthly (FCC) contribution is ${CURRENCY_SYMBOL[currency]}${MONTHLY_MIN}.`);
      return false;
    }
    return true;
  };

  const donorPayload = () => ({
    name: donor.name.trim(),
    email: donor.email.trim(),
    phone: donor.phone.trim(),
    address: donor.address.trim(),
    inspired_by: donor.inspired_by.trim(),
  });

  return (
    <div data-testid="international-donate">
      {/* FCRA disclaimer — appears at the top of the International section */}
      <div
        data-testid="intl-patron-disclaimer-top"
        className="mb-8 border-l-2 border-[#f9d94b] pl-4 py-2"
      >
        <p className="font-sans text-[13px] leading-relaxed text-[#fdfbf7]/80">
          {PATRON_DISCLAIMER}
        </p>
      </div>

      {/* Mode toggle */}
      <div
        role="tablist"
        className="inline-flex border border-[#fdfbf7]/30"
        data-testid="intl-mode-toggle"
      >
        <button
          type="button"
          role="tab"
          aria-selected={mode === "monthly"}
          onClick={() => {
            setMode("monthly");
            setAmount(50);
            setCustom("");
          }}
          data-testid="intl-mode-monthly"
          className={`px-6 py-3 font-mono text-[11px] tracking-[0.15em] uppercase transition-colors ${
            mode === "monthly"
              ? "bg-[#f9d94b] text-[#1a1a1a]"
              : "text-[#fdfbf7]/70 hover:text-[#fdfbf7]"
          }`}
        >
          Become a Monthly Patron
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === "one-time"}
          onClick={() => {
            setMode("one-time");
            setAmount(50);
            setCustom("");
          }}
          data-testid="intl-mode-onetime"
          className={`px-6 py-3 font-mono text-[11px] tracking-[0.15em] uppercase transition-colors ${
            mode === "one-time"
              ? "bg-[#f9d94b] text-[#1a1a1a]"
              : "text-[#fdfbf7]/70 hover:text-[#fdfbf7]"
          }`}
        >
          One-Time
        </button>
      </div>

      {/* Currency */}
      <div className="mt-8">
        <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#fdfbf7]/60 mb-3">
          Currency
        </p>
        <div className="flex gap-2" data-testid="intl-currency">
          {CURRENCIES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCurrency(c)}
              data-testid={`intl-cur-${c}`}
              className={`px-4 py-2 font-mono text-[13px] transition-colors ${
                currency === c
                  ? "bg-[#f9d94b] text-[#1a1a1a]"
                  : "border border-[#fdfbf7]/30 text-[#fdfbf7]/70 hover:border-[#fdfbf7]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Amount */}
      <div className="mt-8">
        <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#fdfbf7]/60 mb-3">
          {mode === "monthly" ? "Monthly amount" : "Amount"}
        </p>
        <div className="grid grid-cols-3 md:grid-cols-5 gap-2" data-testid="intl-amounts">
          {presets.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => {
                setAmount(v);
                setCustom("");
              }}
              data-testid={`intl-amount-${v}`}
              className={`py-3 font-mono text-[14px] transition-colors ${
                !custom && amount === v
                  ? "bg-[#f9d94b] text-[#1a1a1a]"
                  : "border border-[#fdfbf7]/30 text-[#fdfbf7]/80 hover:border-[#fdfbf7]"
              }`}
            >
              {CURRENCY_SYMBOL[currency]}
              {v}
              {mode === "monthly" ? "/mo" : ""}
            </button>
          ))}
        </div>
        <input
          type="number"
          min={mode === "monthly" ? MONTHLY_MIN : 1}
          placeholder={`${CURRENCY_SYMBOL[currency]} Custom${mode === "monthly" ? ` / month (min ${MONTHLY_MIN})` : ""}`}
          data-testid="intl-custom"
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          className="mt-3 bg-transparent border border-[#fdfbf7]/30 px-4 py-3 font-sans text-[15px] text-[#fdfbf7] placeholder:text-[#fdfbf7]/40 focus:outline-none focus:border-[#f9d94b] w-full md:w-56"
        />
        {monthlyMinFail && (
          <p className="mt-2 font-mono text-[11px] tracking-[0.15em] uppercase text-[#f9d94b]" data-testid="intl-monthly-min-warning">
            Monthly minimum is {CURRENCY_SYMBOL[currency]}{MONTHLY_MIN}.
          </p>
        )}
      </div>

      {/* Donor */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="intl-donor">
        <input
          type="text"
          required
          placeholder="Full name *"
          data-testid="intl-donor-name"
          value={donor.name}
          onChange={(e) => setDonor((d) => ({ ...d, name: e.target.value }))}
          className="bg-transparent border border-[#fdfbf7]/30 px-4 py-3 font-sans text-[15px] text-[#fdfbf7] placeholder:text-[#fdfbf7]/40 focus:outline-none focus:border-[#f9d94b]"
        />
        <input
          type="email"
          required
          placeholder="Email *"
          data-testid="intl-donor-email"
          value={donor.email}
          onChange={(e) => setDonor((d) => ({ ...d, email: e.target.value }))}
          className="bg-transparent border border-[#fdfbf7]/30 px-4 py-3 font-sans text-[15px] text-[#fdfbf7] placeholder:text-[#fdfbf7]/40 focus:outline-none focus:border-[#f9d94b]"
        />
        <input
          type="tel"
          placeholder="Phone (with country code)"
          data-testid="intl-donor-phone"
          value={donor.phone}
          onChange={(e) => setDonor((d) => ({ ...d, phone: e.target.value }))}
          className="bg-transparent border border-[#fdfbf7]/30 px-4 py-3 font-sans text-[15px] text-[#fdfbf7] placeholder:text-[#fdfbf7]/40 focus:outline-none focus:border-[#f9d94b]"
        />
        <textarea
          required
          rows={2}
          placeholder="Postal address * (street, city, state, postal code, country)"
          data-testid="intl-donor-address"
          value={donor.address}
          onChange={(e) => setDonor((d) => ({ ...d, address: e.target.value }))}
          className="bg-transparent border border-[#fdfbf7]/30 px-4 py-3 font-sans text-[15px] text-[#fdfbf7] placeholder:text-[#fdfbf7]/40 focus:outline-none focus:border-[#f9d94b] resize-none"
        />
        <div className="md:col-span-2 flex flex-col gap-1.5">
          <input
            type="text"
            placeholder="Which story inspired you to contribute? (optional)"
            data-testid="intl-donor-inspired-by"
            maxLength={140}
            value={donor.inspired_by}
            onChange={(e) => setDonor((d) => ({ ...d, inspired_by: e.target.value }))}
            className="bg-transparent border border-[#fdfbf7]/30 px-4 py-3 font-sans text-[15px] text-[#fdfbf7] placeholder:text-[#fdfbf7]/40 focus:outline-none focus:border-[#f9d94b]"
          />
          <p className="font-sans text-[11px] leading-snug text-[#fdfbf7]/55">
            ⓘ Tell us which of our stories moved you — helps us know what&rsquo;s resonating.
          </p>
        </div>
      </div>

      <p className="mt-4 font-mono text-[10px] tracking-[0.15em] uppercase text-[#fdfbf7]/50">
        * Required · {mode === "monthly" ? "Cancel your membership anytime from PayPal" : "Secure checkout via PayPal"}
      </p>

      {/* Action */}
      <div className="mt-8" data-testid="intl-action">
        {/* FCRA disclaimer — repeated above the PayPal button (last thing they read) */}
        <div
          data-testid="intl-patron-disclaimer-button"
          className="mb-4 border border-[#fdfbf7]/15 bg-[#fdfbf7]/5 p-4"
        >
          <p className="font-sans text-[12px] leading-relaxed text-[#fdfbf7]/70">
            {PATRON_DISCLAIMER}
          </p>
        </div>
        {!PAYPAL_CLIENT_ID ? (
          <div className="border border-[#fdfbf7]/20 p-6">
            <p className="font-sans text-[15px] text-[#fdfbf7]/70">
              PayPal is being configured. Please refresh in a minute or write
              to foundation@storiesbyaradhana.com.
            </p>
          </div>
        ) : mode === "monthly" ? (
          <PayPalScriptProvider options={subscriptionOptions} key={`sub-${currency}`}>
            <PayPalButtons
              style={{ layout: "vertical", shape: "rect", label: "subscribe" }}
              forceReRender={[finalAmount, currency, donor.name, donor.email, donor.address]}
              createSubscription={async () => {
                if (!validateDonor()) throw new Error("donor-info-missing");
                const res = await fetch(`${API}/donate/paypal/create-subscription`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    amount: finalAmount,
                    currency,
                    donor: donorPayload(),
                  }),
                });
                if (!res.ok) {
                  toast.error("Could not start monthly donation. Please try again.");
                  throw new Error("create-subscription failed");
                }
                const data = await res.json();
                window.__sba_paypal_donation_id = data.donation_id;
                return data.subscription_id;
              }}
              onApprove={async (data) => {
                try {
                  const res = await fetch(`${API}/donate/paypal/verify-subscription`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      subscription_id: data.subscriptionID,
                      donation_id: window.__sba_paypal_donation_id,
                    }),
                  });
                  const j = await res.json();
                  if (j.ok) {
                    setSuccessModal({
                      open: true,
                      mode: "monthly",
                      amount: finalAmount * 100,
                      email: donor.email,
                    });
                    setDonor({ name: "", email: "", phone: "", address: "", inspired_by: "" });
                    setCustom("");
                  } else {
                    toast.error(`Subscription ${j.status || "not activated"}. Please try again.`);
                  }
                } catch {
                  toast.error("Could not confirm subscription. Please contact us.");
                }
              }}
              onError={() => {
                toast.error("PayPal encountered an error. Please try again.");
              }}
            />
          </PayPalScriptProvider>
        ) : (
          <PayPalScriptProvider options={oneTimeOptions} key={`ot-${currency}`}>
            <PayPalButtons
              style={{ layout: "vertical", shape: "rect", label: "pay" }}
              forceReRender={[finalAmount, currency, donor.name, donor.email, donor.address]}
              createOrder={async () => {
                if (!validateDonor()) throw new Error("donor-info-missing");
                const res = await fetch(`${API}/donate/paypal/create-order`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    amount: finalAmount,
                    currency,
                    donor: donorPayload(),
                  }),
                });
                if (!res.ok) {
                  toast.error("Could not start PayPal donation. Please try again.");
                  throw new Error("create-order failed");
                }
                const data = await res.json();
                window.__sba_paypal_donation_id = data.donation_id;
                return data.order_id;
              }}
              onApprove={async (data) => {
                try {
                  const res = await fetch(`${API}/donate/paypal/capture-order`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      order_id: data.orderID,
                      donation_id: window.__sba_paypal_donation_id,
                    }),
                  });
                  const j = await res.json();
                  if (j.ok) {
                    setSuccessModal({
                      open: true,
                      mode: "one-time",
                      amount: finalAmount * 100,
                      email: donor.email,
                    });
                    setDonor({ name: "", email: "", phone: "", address: "", inspired_by: "" });
                    setCustom("");
                  } else {
                    toast.error(`Payment ${j.status || "not completed"}. Please try again.`);
                  }
                } catch {
                  toast.error("Could not confirm payment. Please contact us.");
                }
              }}
              onError={() => {
                toast.error("PayPal encountered an error. Please try again.");
              }}
            />
          </PayPalScriptProvider>
        )}
      </div>

      {/* Shareable links */}
      <div className="pt-8 mt-8 border-t border-[#fdfbf7]/15">
        <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#fdfbf7]/55 mb-3">
          Share with someone abroad
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <CopyLinkButton
            url={siteUrl("/patrons-monthly")}
            label="Copy monthly (FCC) link"
            variant="light"
            testId="copy-intl-monthly-link"
          />
          <CopyLinkButton
            url={siteUrl("/patrons-one-time")}
            label="Copy one-time link"
            variant="light"
            testId="copy-intl-onetime-link"
          />
        </div>
      </div>

      <DonationSuccessModal
        open={successModal.open}
        onClose={() => setSuccessModal({ open: false })}
        mode={successModal.mode || "one-time"}
        amount={successModal.amount || 0}
        email={successModal.email || ""}
        currency={successModal.currency || currency}
        isPatron={true}
      />
    </div>
  );
}
